import { apiFetch, ApiFetchError } from "../utils/apiFetch";

/**
 * Diktat (Spracheingabe) über das KMVS-Backend. Die Aufnahme geht an den dort
 * eingestellten Dienst (heute Mistral Voxtral), wird nirgends gespeichert, und
 * zurück kommt nur der Text.
 */

export interface DiktatStatus {
    /** Server hat einen Dienst eingerichtet. */
    verfuegbar: boolean;
    /** Ein Admin hat Diktieren für den Mandanten eingeschaltet. */
    eingeschaltet: boolean;
    dienst: string | null;
    maxSekunden: number;
}

const AUS: DiktatStatus = { verfuegbar: false, eingeschaltet: false, dienst: null, maxSekunden: 300 };

/** Den Knopf gibt es nur, wenn beides stimmt. */
export const diktatNutzbar = (s: DiktatStatus | null | undefined): boolean =>
    !!s && s.verfuegbar && s.eingeschaltet;

let statusAnfrage: Promise<DiktatStatus> | null = null;
const hoerer = new Set<(s: DiktatStatus) => void>();

/** Einmal je Sitzung erfragt und geteilt; Fehler zählen als „aus“. */
export function diktatStatus(): Promise<DiktatStatus> {
    if (!statusAnfrage) {
        statusAnfrage = apiFetch<Partial<DiktatStatus>>("diktat/status")
            .then(({ data }) => ({
                verfuegbar: data?.verfuegbar === true,
                eingeschaltet: data?.eingeschaltet === true,
                dienst: typeof data?.dienst === "string" ? data.dienst : null,
                maxSekunden: Number(data?.maxSekunden) > 0 ? Number(data?.maxSekunden) : 300,
            }))
            .catch(() => {
                // Beim nächsten Aufruf neu fragen — ein Netzaussetzer soll den Knopf nicht für immer nehmen.
                statusAnfrage = null;
                return AUS;
            });
    }
    return statusAnfrage;
}

/** Neuer Stand (nach dem Umschalten in den Einstellungen) — alle Knöpfe ziehen nach. */
export function diktatStatusSetzen(s: DiktatStatus): void {
    statusAnfrage = Promise.resolve(s);
    hoerer.forEach((h) => h(s));
}

export function diktatStatusBeobachten(h: (s: DiktatStatus) => void): () => void {
    hoerer.add(h);
    return () => {
        hoerer.delete(h);
    };
}

/** Nur für Tests. */
export function diktatStatusZuruecksetzen(): void {
    statusAnfrage = null;
    hoerer.clear();
}

/** Admin: Diktieren für den Mandanten ein- oder ausschalten. */
export async function diktatSchalten(eingeschaltet: boolean): Promise<DiktatStatus> {
    const { data } = await apiFetch<DiktatStatus>("diktat/einstellung", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ eingeschaltet }),
    });
    diktatStatusSetzen(data);
    return data;
}

/** Dateiendung passend zum Aufnahmeformat — Mistral erkennt das Format auch am Namen. */
export function endungFuer(mimeType: string): string {
    const t = mimeType.split(";")[0].trim().toLowerCase();
    if (t.endsWith("/mp4") || t.endsWith("/m4a") || t.endsWith("/x-m4a")) return "m4a";
    if (t.endsWith("/ogg")) return "ogg";
    if (t.endsWith("/mpeg")) return "mp3";
    if (t.endsWith("/wav") || t.endsWith("/x-wav")) return "wav";
    return "webm";
}

/** Holt die deutsche Meldung aus einer Fehlerantwort des Backends. */
export function meldungAus(err: unknown): string {
    if (err instanceof ApiFetchError) {
        const roh = err.message.replace(/^HTTP \d+:\s*/, "");
        try {
            const j = JSON.parse(roh) as { message?: unknown };
            if (typeof j.message === "string" && j.message.trim()) return j.message;
        } catch {
            /* kein JSON */
        }
        if (err.status === 413) return "Die Aufnahme ist zu lang. Bitte in kürzeren Stücken diktieren.";
    }
    return "Das Diktat konnte nicht umgewandelt werden. Bitte noch einmal versuchen.";
}

export async function diktatTranskribieren(audio: Blob, signal?: AbortSignal): Promise<string> {
    const typ = audio.type || "audio/webm";
    const form = new FormData();
    form.append("file", audio, `diktat.${endungFuer(typ)}`);
    try {
        const { data } = await apiFetch<{ text?: string }>("diktat/transkribieren", {
            method: "POST",
            body: form,
            signal,
            params: { sprache: "de" },
        });
        return typeof data?.text === "string" ? data.text : "";
    } catch (err) {
        if (err instanceof ApiFetchError && err.status === 503) {
            // Inzwischen ausgeschaltet oder nicht mehr eingerichtet: Knöpfe verschwinden.
            statusAnfrage = null;
        }
        throw err;
    }
}
